import axios from 'axios'
import moment from 'moment'
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react'
import DatePicker from 'react-datepicker'
import SKAlerts, { CreateAlert } from '../../shared/common-alerts/SKAlerts'
import { getStorageValues } from '../../shared/CommonFunctions'
import { getApiBaseUrl } from '../../utils/apiConfig'

const formatCurrency = (amount) => {
  const value = Number(amount) || 0
  try {
    return value.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })
  } catch (_) {
    return `₹${value.toFixed(2)}`
  }
}

const FinanceRegisterFollowup = forwardRef(({ data }, ref) => {
  const [alert, setAlert] = useState(null)
  const [followupList, setFollowupList] = useState([])
  const [followupDate, setFollowupDate] = useState(new Date())
  const [remarks, setRemarks] = useState('')

  const financeRegisterId = data?.FinanceRegisterId
  const userId = useMemo(() => getStorageValues('userId'), [])

  useImperativeHandle(ref, () => ({
    refreshFollowup: fetchFollowups
  }))

  async function fetchFollowups() {
    if (!financeRegisterId) {
      setFollowupList([])
      return
    }
    try {
      const params = {
        UserId: userId,
        FinanceRegisterId: financeRegisterId
      }

      const res = await axios.get(`${getApiBaseUrl()}/financeregister/getfollowupbyid`, { params })

      if (res.status === 200) {
        const payload = res?.data?.data
        const normalized = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.[0])
            ? payload[0]
            : []
        setFollowupList(normalized)
      }
    } catch (err) {
      console.log(err)
      CreateAlert('Problem loading follow-up', 'Error', setAlert)
    }
  }

  async function saveFollowup() {
    if (!financeRegisterId) {
      CreateAlert('Missing finance register reference', 'Error', setAlert)
      return
    }
    if (!remarks.trim()) {
      CreateAlert('Please add remarks', 'Warning', setAlert)
      return
    }

    try {
      const payload = {
        UserId: userId,
        FinanceRegisterId: financeRegisterId,
        FollowupDate: moment(followupDate).format('YYYY-MM-DD HH:mm:ss'),
        FollowupRemarks: remarks
      }

      const res = await axios.post(`${getApiBaseUrl()}/financeregister/savefollowup`, payload)

      if (res.status === 200) {
        setRemarks('')
        fetchFollowups()
        CreateAlert('Follow-up saved!', 'Success', setAlert)
      }
    } catch (err) {
      console.log(err)
      CreateAlert('Problem saving follow-up', 'Error', setAlert)
    }
  }

  useEffect(() => {
    fetchFollowups()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [financeRegisterId])

  const totalVehicleCost = useMemo(() => {
    return Number(data?.VehicleCost) || 0
  }, [data?.VehicleCost])

  return (
    <>
      <SKAlerts alert={alert} />

      <div className="row" style={{ margin: '10px' }}>
        

        <div className="col-lg-12">
                <div className="card">
                  <div className="card-header">
                    <h5 className="card-title">Finance & Loan Details</h5>
                  </div>
                  <div className="card-body">
                    <div className="table-responsive">
                      <table className="table table-bordered mb-0">          
                        <tbody>                     
                          <tr>
                            <td className="bold">Loan Application No</td><td>{data?.LoanApplicationNo || '-'}</td>
                            <td className="bold"></td><td className="bold"></td>
                            <td className="bold"></td><td className="bold"></td>
                          </tr>
                          <tr>
                            <td className="bold">Finance Partner</td><td>{data?.FinancePartnerName || '-'}</td>
                            <td className="bold">Loan Account Number</td><td>{data?.LoanAccountNumber || '-'}</td>
                            <td className="bold">Total Vehicle Cost</td><td>{formatCurrency(totalVehicleCost)}</td>                                                     
                          </tr>
                          <tr>                           
                            <td className="bold">Down Payment</td><td>{formatCurrency(data?.DownPayment)}</td>
                            <td className="bold">Tenure (months)</td><td>{data?.EMITenure || '-'}</td> 
                            <td className="bold">Interest Rate</td><td>{data?.InterestRate || 0}%</td>                                                                                 
                          </tr>
                           <tr>     
                            <td className="bold">Processing Fees</td><td>{formatCurrency(data?.ProcessingFees)}</td>                       
                            <td className="bold">EMI Amount</td><td>{formatCurrency(data?.EMIAmount)}</td>                
                            <td className="bold">Next EMI Date</td><td>{data?.NextEmiDate ? moment(data.NextEmiDate).format('MM/DD/YYYY') : '-'}</td>
                          </tr>
                         
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

        <div className="col-lg-3">
          <label className="form-label">Next Follow-up Date</label>
          <DatePicker
            className="form-control"
            selected={followupDate}
            onChange={(date) => setFollowupDate(date)}
            minDate={new Date()}
          />
        </div>

        <div className="col-lg-9">
          <label className="form-label">Remarks</label>
          <textarea
            className="form-control"
            rows="1"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Add follow-up details..."
          ></textarea>
          <div className="mt-2">
            <button className="btn btn-primary btn-sm me-2" onClick={saveFollowup}>
              Save
            </button>
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => setRemarks('')}
            >
              Cancel
            </button>
          </div>
        </div>

        <div className="col-12">
          <div className="scrollable-comments">
            {followupList?.map((f, i) => (
              <div className="card card-bg-light mg-em" key={f?.FinanceRegisterFollowupId || i}>
                <div className="card-body" style={{ margin: '5px' }}>
                  <div className="d-flex">
                    <div className="me-3">
                      <span className="avatar avatar-rounded">
                        <img
                          alt=""
                          src={`/assets/img/profiles/${f?.UserImagePath || 'avatar-03.jpg'}`}
                        />
                      </span>
                    </div>
                    <div>
                      <div className="fw-semibold">{f?.FullName || '-'}</div>
                      <div dangerouslySetInnerHTML={{ __html: f?.FollowupRemarks || '' }}></div>
                      <small className="text-muted">
                        {f?.CreatedDate
                          ? moment(f.CreatedDate).format('MM/DD/YYYY, h:mm A')
                          : ''}
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {(!followupList || followupList.length === 0) && (
              <div className="text-muted small">No follow-ups available.</div>
            )}
          </div>
        </div>
      </div>
    </>
  )
})

export default FinanceRegisterFollowup
